import type { PersonalRun } from '../score/PersonalRuns';
import { formatTime } from '../utils/format';
import { MenuButtons } from './MenuButtons';
import { button, el, setHidden, setText } from './dom';

export class ResultsScreen {
  readonly root = el('div', 'overlay overlay--modal');
  private readonly rows = el('div', 'results__rows');
  private readonly category = el('p', 'menu-card__sub');
  private readonly status = el('p', 'results__status');
  private readonly input = el('input', 'results__input');
  private readonly watch: HTMLButtonElement;
  private readonly close: HTMLButtonElement;
  private visible = false;
  private reveals: (() => void)[] = [];
  buttons: MenuButtons;

  constructor(onClose: () => void, onWatch: (id: string) => void) {
    this.input.type = 'text';
    this.input.maxLength = 80;
    this.input.placeholder = 'Syötä replay-id';
    this.input.autocomplete = 'off';
    this.input.spellcheck = false;
    this.input.setAttribute('aria-label', 'Replay-id');
    this.input.addEventListener('keydown', (event) => {
      if (event.key === 'Escape') return;
      event.stopPropagation();
      if (event.key === 'Enter') { event.preventDefault(); this.watch.click(); }
    });
    this.watch = button('KATSO DEMO', () => onWatch(this.input.value.trim()), 'btn btn--primary');
    this.close = button('TAKAISIN', onClose, 'btn', 'ESC');
    this.buttons = new MenuButtons([this.watch, this.close]);
    const form = el('div', 'results__form');
    form.append(this.input, this.watch);
    const card = el('div', 'menu-card results');
    card.append(el('h2', 'menu-card__title', 'TULOKSET'), this.category,
      el('p', 'menu-card__sub', 'Omat top 10 · tässä selaimessa. Nuolet valitsevat, Enter näyttää tai piilottaa replay-id:n.'),
      this.rows,
      el('p', 'menu-card__sub', 'Jaettava replay-id on salainen, kunnes annat sen itse. local_-tunniste toimii vain tässä selaimessa.'),
      form, this.status, this.close);
    this.status.setAttribute('role', 'status');
    this.root.append(card);
    this.root.addEventListener('mousedown', (event) => event.stopPropagation());
  }

  mount(parent: HTMLElement): void { parent.append(this.root); }

  show(rows: PersonalRun[], category: string): void {
    setText(this.category, category);
    this.rows.replaceChildren();
    this.reveals = [];
    const buttons = rows.map((row, index) => {
      const id = row.replayId ?? (row.replay ? row.id : null);
      let revealed = false;
      const value = el('span', 'results__id');
      const node = button(`${index + 1}.  ${formatTime(row.result.timeMs)}`, () => {
        if (!id || window.getSelection()?.toString()) return;
        revealed = !revealed;
        setText(value, revealed ? id : '•••••••• · ENTER');
        node.setAttribute('aria-expanded', String(revealed));
      }, 'btn results__row');
      const conceal = () => {
        revealed = false;
        setText(value, id ? '•••••••• · ENTER' : 'Ei replayta');
        node.setAttribute('aria-expanded', 'false');
      };
      conceal();
      this.reveals.push(conceal);
      node.append(value);
      this.rows.append(node);
      return node;
    });
    if (!rows.length) this.rows.append(el('p', 'menu-card__sub', 'Ei vielä tuloksia tällä pelikonfiguraatiolla.'));
    // This makes the input reachable with the same arrow navigation as the rows.
    const enterId = button('SYÖTÄ REPLAY-ID', () => this.input.focus());
    this.rows.append(enterId);
    this.buttons = new MenuButtons([...buttons, enterId, this.watch, this.close], true);
    this.buttons.setActive(this.visible);
  }

  setStatus(message: string, loading = false): void {
    setText(this.status, message);
    this.watch.disabled = loading;
  }

  setVisible(visible: boolean): void {
    if (!visible && this.visible) {
      this.reveals.forEach((conceal) => conceal());
      this.input.value = '';
      if (this.root.contains(document.activeElement)) (document.activeElement as HTMLElement).blur();
    }
    this.visible = visible;
    setHidden(this.root, !visible);
    this.buttons.setActive(visible);
  }
}
